import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  BarChart3,
  Check,
  Copy,
  Globe,
  Hash,
  Lock,
  Mail,
  PhoneCall,
  PhoneForwarded,
  PhoneMissed,
  Shield,
  Trash2,
} from 'lucide-react';
import { PageHeader, Button, ConfirmDialog, Input } from '@/shared/ui';
import { useLocation } from '@/shared/hooks/useLocation';
import { formatPhoneDisplay } from '@/shared/utils/validation';
import patterns from '@/shared/ui/styles/patterns.module.css';
import styles from '../pages/AgentsPage.module.css';
import {
  resolveTemplate,
  PHONE_PROVIDERS,
  FORWARDING_STEPS,
  isValidAreaCode,
} from '../agentShared';
import type { DraftAgent, SectionKey } from '../agentShared';
import type { AgentConfig, AvailableNumber } from '../api/callsApi';
import { SectionCard } from './builderShared';
import { useCanManageCalls } from '../hooks/useCanManageCalls';
import {
  AlertsSection,
  CallbackLanguageSection,
  HandoffSection,
  KnowledgeSection,
  PolicySection,
  RoutingSection,
  ScriptSection,
  ValueSection,
} from './sections';
import { SourcesSection, OfferSection } from './SourcesSection';

// Mirrors the backend's renderOpeningLine for the preview: office-name tokens
// are filled from the location; per-call ones (e.g. the dialled source's offer)
// are shown as a readable placeholder.
function previewGreeting(greeting: string, officeName: string): string {
  return greeting.replace(/\{\{\s*([a-z_]+)\s*\}\}/gi, (_m, key: string) => {
    const k = key.toLowerCase();
    if (['practice_name', 'dental_office_name', 'office_name'].includes(k) && officeName) {
      return officeName;
    }
    return `[${k.replace(/_/g, ' ')}]`;
  });
}

const GUARDRAILS = [
  'No clinical diagnosis or medical advice',
  'No symptom-severity assessment (routing only)',
  'No access to or writing into patient records',
  'Callers are told up front that the call is recorded (required in two-party-consent states like CA)',
  'Life-threatening emergency → instruct caller to call 911 (locked)',
];

// ── Builder ───────────────────────────────────────────────────────────────────

export function AgentBuilder({
  draft,
  setDraft,
  onBack,
  onSave,
  onActivate,
  onPause,
  onClone,
  onDelete,
  isSaving,
  isActivating,
  isPausing = false,
  isCloning,
  isDeleting,
  availableNumbers,
}: {
  draft: DraftAgent;
  setDraft: (fn: (prev: DraftAgent) => DraftAgent) => void;
  onBack: () => void;
  onSave: () => void;
  onActivate: (existingPhoneNumber?: string) => void;
  onPause: () => void;
  onClone: () => void;
  onDelete: () => void;
  isSaving: boolean;
  isActivating: boolean;
  isPausing?: boolean;
  isCloning: boolean;
  isDeleting: boolean;
  availableNumbers: AvailableNumber[];
}) {
  const template = resolveTemplate(draft.templateKey) ?? resolveTemplate('custom')!;
  const navigate = useNavigate();
  const { currentLocation } = useLocation();
  const officeName = currentLocation?.name ?? '';
  const canManage = useCanManageCalls();

  const set = (key: keyof DraftAgent, value: unknown) =>
    setDraft((prev) => ({ ...prev, [key]: value }));
  const setConfig = <K extends keyof AgentConfig>(key: K, value: AgentConfig[K]) =>
    setDraft((prev) => ({ ...prev, config: { ...prev.config, [key]: value } }));

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPauseConfirm, setShowPauseConfirm] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [selectedNumber, setSelectedNumber] = useState<string>('new');
  const [openSections, setOpenSections] = useState<Partial<Record<SectionKey, boolean>>>({});
  const isOpen = (key: SectionKey) => openSections[key] ?? true;
  const toggleSection = (key: SectionKey) =>
    setOpenSections((prev) => ({ ...prev, [key]: !isOpen(key) }));

  const handleActivateClick = () => {
    // Agent already has a number (resuming paused) — re-attach silently, no modal
    if (draft.trackingNumber) {
      onActivate();
      return;
    }
    // No number yet — always show the approval step so the user consciously
    // confirms before anything is purchased (PRD §4.5).
    setSelectedNumber('new');
    setShowActivateModal(true);
  };

  const needsKnowledge = template.sections.includes('knowledge');
  const scriptDone = !!(draft.name.trim() && draft.greeting.trim());
  const knowDone = !needsKnowledge || !!(draft.hours.trim() && draft.address.trim());
  const routeDone = draft.notifyEmails.length > 0;
  const allReady = scriptDone && knowDone && routeDone;
  const isActive = draft.status === 'active';
  const isPaused = draft.status === 'paused';
  const hasRouting = template.sections.includes('routing');

  const sectionProps = { draft, template, set, setConfig };

  const renderSection = (key: SectionKey) => {
    const openProps = { open: isOpen(key), onToggle: () => toggleSection(key) };
    switch (key) {
      case 'script':
        return <ScriptSection key={key} {...sectionProps} {...openProps} />;
      case 'routing':
        return <RoutingSection key={key} {...sectionProps} {...openProps} />;
      case 'handoff':
        return <HandoffSection key={key} {...sectionProps} {...openProps} />;
      case 'sources':
        return (
          <SourcesSection key={key} agentId={draft.id} areaCode={draft.areaCode} {...openProps} />
        );
      case 'offer':
        return <OfferSection key={key} agentId={draft.id} {...openProps} />;
      case 'knowledge':
        return <KnowledgeSection key={key} {...sectionProps} {...openProps} />;
      case 'policy':
        return <PolicySection key={key} {...sectionProps} {...openProps} />;
      case 'callbackLanguage':
        return <CallbackLanguageSection key={key} {...sectionProps} {...openProps} />;
      case 'alerts':
        return <AlertsSection key={key} {...sectionProps} {...openProps} />;
      case 'value':
        return <ValueSection key={key} {...sectionProps} {...openProps} />;
      default:
        return null;
    }
  };

  return (
    <div>
      <button className={styles.backLink} onClick={onBack}>
        <ArrowLeft size={13} /> Back to agents
      </button>
      <PageHeader
        title={draft.name || 'New use case'}
        subtitle={`${template.blurb} Safety rules are locked on.`}
        actions={
          <div className={styles.actionRow}>
            {template.id === 'campaign' && draft.id && (
              <Button
                variant="secondary"
                leftIcon={<BarChart3 size={14} />}
                onClick={() => navigate('/calls/sources')}
              >
                View performance
              </Button>
            )}
            {!canManage && (
              <span className={styles.hintText}>
                View only — a manager, admin or owner can change this agent.
              </span>
            )}
            {canManage && !!draft.id && !draft.isProtected && (
              <Button
                variant="destructive"
                onClick={() => setShowDeleteConfirm(true)}
                loading={isDeleting}
                leftIcon={<Trash2 size={14} />}
              >
                Delete
              </Button>
            )}
            {canManage && draft.id && (
              <Button
                variant="secondary"
                onClick={onClone}
                loading={isCloning}
                leftIcon={<Copy size={14} />}
              >
                Clone
              </Button>
            )}
            {!canManage ? null : isActive ? (
              <Button
                variant="secondary"
                onClick={() => setShowPauseConfirm(true)}
                loading={isPausing}
              >
                Pause
              </Button>
            ) : (
              <Button
                onClick={handleActivateClick}
                disabled={!allReady}
                loading={isActivating}
                leftIcon={<Check size={14} />}
              >
                {isPaused ? 'Resume' : 'Activate'}
              </Button>
            )}
            {canManage && (
              <Button onClick={onSave} loading={isSaving}>
                {draft.trackingNumber ? 'Publish' : 'Save Draft'}
              </Button>
            )}
          </div>
        }
      />
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => {
          setShowDeleteConfirm(false);
          onDelete();
        }}
        title="Delete agent?"
        message={
          draft.trackingNumber
            ? `This will detach the tracked number ${draft.trackingNumber} (it stays available for reassignment), delete the Retell agent, and remove this use case. This can't be undone.`
            : "This will permanently remove the agent. This can't be undone."
        }
        confirmLabel="Delete agent"
        variant="destructive"
        isLoading={isDeleting}
      />
      <ConfirmDialog
        isOpen={showPauseConfirm}
        onClose={() => setShowPauseConfirm(false)}
        onConfirm={() => {
          setShowPauseConfirm(false);
          onPause();
        }}
        title="Pause agent?"
        message={
          draft.trackingNumber
            ? `While paused, calls to ${formatPhoneDisplay(draft.trackingNumber)}${
                template.id === 'campaign' ? ' and its source numbers' : ''
              } won't be answered by anyone — the fallback number only rings when too many calls come in at once. Forward the line elsewhere first if callers still need to reach you.`
            : 'The agent will stop answering calls until you resume it.'
        }
        confirmLabel="Pause agent"
        variant="destructive"
        isLoading={isPausing}
      />
      <ConfirmDialog
        isOpen={showActivateModal}
        onClose={() => setShowActivateModal(false)}
        onConfirm={() => {
          setShowActivateModal(false);
          onActivate(selectedNumber === 'new' ? undefined : selectedNumber);
        }}
        title={isPaused ? 'Resume agent' : 'Approve tracking number'}
        message={
          <div className={styles.activateModalBody}>
            <p className={styles.activateModalLabel}>
              Choose the tracked number this agent answers. Nothing is purchased or pointed at the
              agent until you approve.
            </p>
            <select
              className={styles.activateSelect}
              value={selectedNumber}
              onChange={(e) => setSelectedNumber(e.target.value)}
            >
              <option value="new">
                Buy a new number{draft.areaCode ? ` (${draft.areaCode} area code)` : ''}
              </option>
              {availableNumbers.map((n) => (
                <option key={n.id} value={n.number}>
                  Reuse {n.number} — from: {n.agentName ?? 'a previous agent'}
                </option>
              ))}
            </select>
            {selectedNumber === 'new' && (
              <Input
                label="Area code for the new number"
                value={draft.areaCode}
                onChange={(e) => set('areaCode', e.target.value.replace(/\D/g, '').slice(0, 3))}
                placeholder="e.g. 512"
                inputMode="numeric"
                error={
                  draft.areaCode && !isValidAreaCode(draft.areaCode)
                    ? 'Enter a 3-digit US area code'
                    : undefined
                }
                hint="Use the area code your patients recognize — usually your office's own."
                fullWidth
              />
            )}
          </div>
        }
        confirmLabel={isPaused ? 'Resume' : 'Approve & activate'}
        confirmDisabled={selectedNumber === 'new' && !isValidAreaCode(draft.areaCode)}
        isLoading={isActivating}
      />

      <div className={styles.builderLayout}>
        <div className={styles.builderMain}>
          {template.sections.map(renderSection)}

          <SectionCard icon={Shield} title="Safety rules" sub="always on" done locked>
            <div className={styles.guardrailList}>
              {GUARDRAILS.map((g) => (
                <div key={g} className={styles.guardrailItem}>
                  <Lock size={12} className={styles.guardrailIcon} /> {g}
                </div>
              ))}
            </div>
          </SectionCard>
        </div>

        {/* Sidebar */}
        <div className={styles.builderSidebar}>
          <div className={`${patterns.panel} ${styles.sidebarCard}`}>
            <div className={patterns.kicker}>
              <Hash size={12} /> Tracked number
            </div>
            {draft.trackingNumber ? (
              <>
                <div className={styles.numberValue}>{formatPhoneDisplay(draft.trackingNumber)}</div>
                <span className={styles.successMeta}>
                  <Check size={11} /> Provisioned · {draft.areaCode} area code
                </span>
              </>
            ) : (
              <p className={styles.pendingText}>
                Pending — provisioned when you activate.{' '}
                {draft.areaCode
                  ? `We'll propose a number in the ${draft.areaCode} area code (you can change it when approving); nothing is set up until you approve it.`
                  : "You'll pick the area code when approving; nothing is set up until then."}
              </p>
            )}
          </div>

          {draft.trackingNumber && (
            <div className={`${patterns.panel} ${styles.sidebarCard}`}>
              <div className={patterns.kicker}>
                <PhoneMissed size={12} /> Fallback number
              </div>
              <p className={styles.pendingText}>
                Retell rings this number when too many calls arrive at once for the agent to take
                another. It doesn't cover a paused agent — a paused number isn't answered.
              </p>
              {draft.fallbackNumber ? (
                <div className={styles.numberValue}>{formatPhoneDisplay(draft.fallbackNumber)}</div>
              ) : (
                <p className={styles.pendingText}>
                  Not set — add one from the{' '}
                  <Link to="/settings/operations" className={styles.inlineLink}>
                    tracking numbers in Settings → Operations
                  </Link>
                  .
                </p>
              )}
            </div>
          )}

          {hasRouting && (
            <div className={`${patterns.panel} ${styles.sidebarCard}`}>
              <div className={patterns.kicker}>
                <PhoneForwarded size={12} /> Forwarding setup
              </div>
              <label className={styles.fieldLabel}>Your phone provider</label>
              <select
                className={styles.activateSelect}
                value={draft.config.phoneProvider ?? PHONE_PROVIDERS[0]}
                onChange={(e) => setConfig('phoneProvider', e.target.value)}
              >
                {PHONE_PROVIDERS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
              <ol className={styles.stepsList}>
                {FORWARDING_STEPS[
                  (draft.config.phoneProvider ??
                    PHONE_PROVIDERS[0]) as (typeof PHONE_PROVIDERS)[number]
                ]?.map((step) => <li key={step}>{step}</li>) ?? (
                  <li>Ask your phone provider to forward calls to your tracked number.</li>
                )}
                <li>
                  {draft.trackingNumber
                    ? `Forward to ${formatPhoneDisplay(draft.trackingNumber)}.`
                    : 'Your tracked number appears here once the agent is activated.'}
                </li>
              </ol>
              <p className={styles.pendingText}>
                Test it: from a cell phone, call your office line and let it ring out (or call while
                the line is busy). The agent should answer, and the call will show in the Call Log.
              </p>
            </div>
          )}

          <div className={`${patterns.panel} ${styles.sidebarCard}`}>
            <div className={patterns.kicker}>
              <PhoneCall size={12} /> Preview
            </div>
            {draft.greeting ? (
              <div className={styles.previewBox}>
                "{previewGreeting(draft.greeting, officeName)}"
              </div>
            ) : (
              <div className={`${styles.previewBox} ${styles.previewBoxMuted}`}>
                Opening line will appear here…
              </div>
            )}
            <div className={styles.previewMeta}>
              {draft.languages.length > 0 && (
                <span className={styles.metaRow}>
                  <Globe size={11} color="var(--info)" /> {draft.languages.join(', ')}
                </span>
              )}
              {draft.notifyEmails.length > 0 && (
                <span className={styles.metaRow}>
                  <Mail size={11} color="var(--success)" /> Summary → {draft.notifyEmails[0]}
                  {draft.notifyEmails.length > 1 ? ` +${draft.notifyEmails.length - 1} more` : ''}
                </span>
              )}
              {draft.transferNumber && (
                <span className={styles.metaRow}>
                  <PhoneForwarded size={11} /> Transfers to {draft.transferNumber}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
